import { useCallback, useEffect, useRef } from "react";
import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { isClientResponseError } from "@/lib/errors";
import { pb } from "@/lib/pb";
import type { CardsResponse, ProjectsResponse } from "@/lib/pb-types";
import { queryKeys } from "@/lib/queryKeys";
import { useRealtimeCollection } from "@/lib/realtime";
import { playSfx } from "@/platform";
import {
  applyMove,
  nextIssueKey,
  type BoardCard,
  type MovePlan,
  type Ordered,
} from "./board";
import { ACTIVE_PROJECT_CARDS, activeProjects } from "./archive";
import type { LaneStatus } from "./lanes";

/**
 * The kanban data layer.
 *
 * Every query is keyed with `queryKeys.collection(...)` so a realtime event on
 * `cards` or `projects` invalidates it by prefix — a query keyed any other way
 * would quietly stop updating when another device moved a card.
 *
 * Writes are optimistic. Dropping a card has to land instantly under the
 * cursor; waiting for a round trip is what makes a board feel floaty, and this
 * board is explicitly meant not to.
 */

/** Shared by every kanban mutation so the realtime listener can stand down. */
const KANBAN_MUTATION_KEY = ["kanban"] as const;

// `networkMode: "always"` — so an unreachable server errors rather than
// pausing — is set once for the whole app in `lib/queryClient.ts`, not per
// query here. See the comment there for why.

const CARDS_KEY = queryKeys.collection("cards");
const PROJECTS_KEY = queryKeys.collection("projects");

function cardsKey(projectId: string | null) {
  return queryKeys.collection("cards", { project: projectId });
}

/* -------------------------------------------------------------------------- */
/* Queries                                                                    */

function fetchProjects() {
  return pb.collection("projects").getFullList<ProjectsResponse>({
    sort: "order,name",
    requestKey: null,
  });
}

/** Module-level so `select` keeps one identity and its result stays memoised. */
const selectActive = (projects: ProjectsResponse[]) => activeProjects(projects);

/**
 * The active projects — the ones every surface lists, counts and draws.
 *
 * Archived boards (`archive.ts`) are dropped by `select`, on the same cache
 * entry `useAllProjects` reads, so the two can never disagree and archiving
 * costs no second request.
 */
export function useProjects() {
  return useQuery({
    queryKey: PROJECTS_KEY,
    queryFn: fetchProjects,
    select: selectActive,
  });
}

/**
 * Every project, archived ones included. Only for the places that must still
 * see a put-away board: the picker's Archived section, slug uniqueness, and
 * resolving a name for a record that points at one.
 */
export function useAllProjects() {
  return useQuery({
    queryKey: PROJECTS_KEY,
    queryFn: fetchProjects,
  });
}

/**
 * Every card of one project in a single request.
 *
 * The heaviest real board is 95 cards, so paging would buy nothing but seams:
 * the lanes need the whole set anyway to count and to order themselves.
 */
export function useProjectCards(projectId: string | null) {
  return useQuery({
    queryKey: cardsKey(projectId),
    enabled: projectId !== null,
    queryFn: () =>
      pb.collection("cards").getFullList<CardsResponse<unknown>>({
        filter: pb.filter("project = {:project}", { project: projectId }),
        sort: "order,issue_key",
        requestKey: null,
      }),
  });
}

/**
 * Every card of every project in a single request, for the Agents desk's source
 * links. Heavy: it brings every field of every card, so no board may use it.
 *
 * Keyed under `queryKeys.collection("cards", "all")`, so `useBoardRealtime`'s
 * invalidation reaches it with no realtime wiring of its own.
 */
export function useAllCards() {
  return useQuery({
    queryKey: queryKeys.collection("cards", "all"),
    queryFn: () =>
      pb.collection("cards").getFullList<CardsResponse<unknown>>({
        // Archived boards' cards stay on the server (`archive.ts`).
        filter: ACTIVE_PROJECT_CARDS,
        sort: "order,issue_key",
        requestKey: null,
      }),
  });
}

export type ProjectCounts = { total: number; open: number; blocking: number; inReview: number };

/**
 * Card counts for every project at once.
 *
 * One request over two columns rather than a count query per project: 454 rows
 * of `{status}` is a few kilobytes, and it means the picker's numbers can never
 * disagree with the board's. `inReview` backs the dashboard's second
 * attention row (TASK-2 D5) — it rides this same request rather than opening
 * a new one, per that ticket's "no new query may appear".
 */
export function useProjectCounts() {
  return useQuery({
    queryKey: queryKeys.collection("cards", "counts"),
    queryFn: async () => {
      const rows = await pb.collection("cards").getFullList<{ project: string; status: string }>({
        fields: "project,status",
        // An archived board counts for nothing anywhere — picker, dashboard.
        filter: ACTIVE_PROJECT_CARDS,
        requestKey: null,
      });
      const counts = new Map<string, ProjectCounts>();
      for (const row of rows) {
        const entry = counts.get(row.project) ?? { total: 0, open: 0, blocking: 0, inReview: 0 };
        entry.total += 1;
        if (row.status !== "done" && row.status !== "archived") {
          entry.open += 1;
        }
        if (row.status === "blocking") {
          entry.blocking += 1;
        }
        if (row.status === "in-review") {
          entry.inReview += 1;
        }
        counts.set(row.project, entry);
      }
      return counts;
    },
  });
}

/**
 * Keep the board live, but not while it is being written to.
 *
 * A renumbered lane is many PATCHes and therefore many realtime events; letting
 * each one refetch mid-write would show the board halfway through its own
 * reshuffle. The mutations invalidate on settle, so nothing is lost by waiting.
 */
export function useBoardRealtime(): void {
  const queryClient = useQueryClient();
  const writing = useIsMutating({ mutationKey: KANBAN_MUTATION_KEY }) > 0;
  const writingRef = useRef(writing);
  writingRef.current = writing;

  const onEvent = useCallback(() => {
    if (!writingRef.current) {
      void queryClient.invalidateQueries({ queryKey: CARDS_KEY });
    }
  }, [queryClient]);

  useRealtimeCollection("cards", { invalidate: false, onEvent });
  // A project event can be an archive or unarchive, and the card reads filter
  // through `project.archived_at` — so they have to refetch too.
  useRealtimeCollection("projects", { onEvent });

  // A write that finished while events were being ignored still has to land.
  // Only the busy → idle transition triggers it: invalidating on mount as well
  // would make every visit to the board fetch its cards twice.
  const wasWriting = useRef(writing);
  useEffect(() => {
    if (wasWriting.current && !writing) {
      void queryClient.invalidateQueries({ queryKey: CARDS_KEY });
    }
    wasWriting.current = writing;
  }, [writing, queryClient]);
}

/* -------------------------------------------------------------------------- */
/* Writes                                                                     */

/** Fields a human can edit on a card. */
export type CardValues = {
  title: string;
  description: string;
  status: LaneStatus;
  priority: string;
  type: string;
  owner: string;
  /** PocketBase date string, or "" to clear it. */
  due_date: string;
  tags: string[];
};

type CreateCardInput = {
  values: CardValues;
  order: number;
  images: readonly File[];
};

/**
 * A card and its first images are one create request.
 *
 * Creating the JSON record and appending files afterwards leaves an awkward
 * half-created card if the upload fails. PocketBase accepts a multipart create,
 * so the server can validate and persist both together. JSON fields need their
 * explicit wire representation because `FormData` only carries strings/files.
 */
function createCardBody(
  input: CreateCardInput,
  projectId: string,
  issueKey: string,
): FormData | Record<string, unknown> {
  const base = {
    ...input.values,
    project: projectId,
    issue_key: issueKey,
    order: input.order,
  };
  if (input.images.length === 0) {
    return base;
  }

  const body = new FormData();
  for (const [field, value] of Object.entries(base)) {
    body.append(field, Array.isArray(value) ? JSON.stringify(value) : String(value));
  }
  for (const image of input.images) {
    body.append("images", image, image.name);
  }
  return body;
}

/**
 * Write a batch of `order` values.
 *
 * PocketBase's transactional batch endpoint is off by default, so this is
 * deliberately plain PATCHes — bounded concurrency rather than a single
 * request, which keeps the write path working against an untouched server.
 */
async function writeOrders(entries: readonly Ordered[], concurrency = 6): Promise<void> {
  if (entries.length === 0) {
    return;
  }
  let cursor = 0;
  const workers = Array.from({ length: Math.min(concurrency, entries.length) }, async () => {
    for (;;) {
      const index = cursor;
      cursor += 1;
      const entry = entries[index];
      if (!entry) {
        return;
      }
      await pb.collection("cards").update(entry.id, { order: entry.order }, { requestKey: null });
    }
  });
  await Promise.all(workers);
}

/** One `getQueriesData` row: the key a cache entry lives under, and its data. */
type CardsQueryEntry = [readonly unknown[], unknown];

/**
 * The board's write API.
 *
 * Each mutation patches the cache before the request goes out and restores
 * the exact previous state if the server refuses, so a failed drag snaps back
 * instead of leaving the board lying about where a card is.
 *
 * ## Every mutation patches every cached card list, not one key
 *
 * A card is not always in exactly one cache entry. `useProjectCards` keys by
 * project; `useAllCards` (TASK-2 D1) holds every project's cards under one
 * key with no project filter; `CardPeek` opens whichever per-project query
 * its card belongs to. All three sit under the shared `CARDS_KEY` prefix, and
 * in All Projects mode the *same* card is live in two of them at once — the
 * all-cards list and the per-project list a peek fetches. A snapshot/patch
 * keyed to a single `cardsKey(projectId)` would only ever touch one of those,
 * so an edit made from the all-cards board would not appear in the peek's own
 * list (or vice versa) until the round trip settled — silently breaking this
 * app's "writes are optimistic everywhere" contract the moment a second cache
 * of the same card existed.
 *
 * So every mutation below snapshots and patches with `getQueriesData` /
 * `setQueriesData({ queryKey: CARDS_KEY })` instead of a single get/set pair.
 * The one hazard that creates: `queryKeys.collection("cards", "counts")`
 * (`useProjectCounts`) also lives under `CARDS_KEY`, and its cached value is a
 * `Map<string, ProjectCounts>`, not a `BoardCard[]`. `patchAll` below guards
 * for exactly this — anything that is not an array is returned untouched
 * rather than handed to a card-list updater that would crash calling `.map`
 * or `.filter` on a `Map`.
 *
 * ## Undo is per-card, not a snapshot of the whole prefix
 *
 * Sharing `CARDS_KEY` also means two independent mutations can be in flight
 * at once — a lane move on one board and a `CardPeek` save on an unrelated
 * project's card, say — each with its own `useBoardWrites`
 * instance but the *same* `QueryClient`. If `onError` simply restored the
 * entire pre-mutation snapshot (every entry, verbatim), a failure in the
 * first mutation would wipe out the second one's already-applied optimistic
 * patch too — even though the second write may already have succeeded on the
 * server — because "restore the snapshot" does not know the second write
 * exists. That is reachable, not hypothetical: nothing here serializes two
 * `useBoardWrites` instances against each other.
 *
 * So `onMutate` records only the *previous value of the specific card(s)
 * this mutation is about to touch* (`snapshotCards`, from the same
 * `getQueriesData` snapshot `patchAll` is about to overwrite), and `onError`
 * (`restoreCards`) puts back only those cards, in every cache list that holds
 * them — leaving every other card, including one a concurrent mutation is
 * mid-flight on, untouched. The residual risk this leaves is narrower and
 * pre-existing: two mutations racing on the *very same* card can still step
 * on each other, exactly as they could before TASK-2 when every project had
 * its own single cache key. Widening to `CARDS_KEY` no longer widens the
 * blast radius beyond that.
 */
type CardSnapshot = Map<string, BoardCard>;

/** The first occurrence of each id in `entries`, read before this mutation's own patch runs. */
function snapshotCards(entries: CardsQueryEntry[], ids: Iterable<string>): CardSnapshot {
  const wanted = new Set(ids);
  const found: CardSnapshot = new Map();
  for (const [, data] of entries) {
    if (!Array.isArray(data)) {
      continue;
    }
    for (const card of data as BoardCard[]) {
      if (wanted.has(card.id) && !found.has(card.id)) {
        found.set(card.id, card);
      }
    }
  }
  return found;
}

export function useBoardWrites(projectId: string | null) {
  const queryClient = useQueryClient();
  const key = cardsKey(projectId);

  /** Cancels in-flight fetches (so they can't clobber the optimistic patch) and reads every cached card list. */
  const snapshotAll = useCallback(async (): Promise<CardsQueryEntry[]> => {
    await queryClient.cancelQueries({ queryKey: CARDS_KEY });
    return queryClient.getQueriesData<unknown>({ queryKey: CARDS_KEY });
  }, [queryClient]);

  /** Apply `updater` to every cached card list; leave anything else (the counts `Map`) alone. */
  const patchAll = useCallback(
    (updater: (cards: BoardCard[]) => BoardCard[]) => {
      queryClient.setQueriesData<unknown>({ queryKey: CARDS_KEY }, (data: unknown) =>
        Array.isArray(data) ? updater(data as BoardCard[]) : data,
      );
    },
    [queryClient],
  );

  /**
   * Put back only the cards named in `previous` — reviving one that a failed
   * `remove` had filtered out just as readily as reverting one whose fields
   * a failed `save`/`move` had changed. Every other card in every
   * cached list — including one a *different*, still-in-flight mutation has
   * already optimistically patched — is left exactly as it is. See "Undo is
   * per-card" above for why this must not be a whole-snapshot restore.
   */
  const restoreCards = useCallback(
    (previous: CardSnapshot | undefined) => {
      if (!previous) {
        return;
      }
      patchAll((cards) => {
        const present = new Set(cards.map((card) => card.id));
        const revived = cards.map((card) => previous.get(card.id) ?? card);
        for (const [id, card] of previous) {
          if (!present.has(id)) {
            revived.push(card);
          }
        }
        return revived;
      });
    },
    [patchAll],
  );

  const settle = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: CARDS_KEY });
  }, [queryClient]);

  const move = useMutation({
    mutationKey: KANBAN_MUTATION_KEY,
    mutationFn: async (plan: MovePlan) => {
      await pb.collection("cards").update(
        plan.card.id,
        { status: plan.status, order: plan.order },
        { requestKey: null },
      );
      await writeOrders(plan.renumber);
    },
    onMutate: async (plan) => {
      const entries = await snapshotAll();
      const previous = snapshotCards(entries, [plan.card.id, ...plan.renumber.map((entry) => entry.id)]);
      patchAll((cards) => applyMove(cards, plan));
      return { previous };
    },
    onError: (_error, _plan, context) => restoreCards(context?.previous),
    onSettled: settle,
  });

  const save = useMutation({
    mutationKey: KANBAN_MUTATION_KEY,
    mutationFn: async (input: {
      card: BoardCard;
      values: CardValues;
      order?: number;
      renumber?: Ordered[];
    }) => {
      const body: Record<string, unknown> = { ...input.values };
      if (input.order !== undefined) {
        body.order = input.order;
      }
      const updated = await pb
        .collection("cards")
        .update<BoardCard>(input.card.id, body, { requestKey: null });
      await writeOrders(input.renumber ?? []);
      return updated;
    },
    onMutate: async (input) => {
      const entries = await snapshotAll();
      const renumbered = new Map((input.renumber ?? []).map((entry) => [entry.id, entry.order]));
      const previous = snapshotCards(entries, [input.card.id, ...renumbered.keys()]);
      patchAll((cards) =>
        cards.map((card) => {
          if (card.id === input.card.id) {
            return {
              ...card,
              ...input.values,
              order: input.order ?? card.order,
            } as BoardCard;
          }
          const order = renumbered.get(card.id);
          return order === undefined ? card : { ...card, order };
        }),
      );
      return { previous };
    },
    onError: (_error, _input, context) => restoreCards(context?.previous),
    onSettled: settle,
  });

  /**
   * Create a card, minting the next key for the project.
   *
   * Keys are unique per project, so two clients creating at once will collide;
   * the loser retries with the next number rather than dropping the card.
   */
  const create = useMutation({
    mutationKey: KANBAN_MUTATION_KEY,
    mutationFn: async (input: CreateCardInput) => {
      if (!projectId) {
        throw new Error("Pick a project before creating a card.");
      }
      const existing = queryClient.getQueryData<BoardCard[]>(key) ?? [];
      let attempt = 0;
      let issueKey = nextIssueKey(existing);
      for (;;) {
        try {
          return await pb.collection("cards").create<BoardCard>(
            createCardBody(input, projectId, issueKey),
            { requestKey: null },
          );
        } catch (error) {
          const collision =
            isClientResponseError(error) &&
            error.status === 400 &&
            Boolean(error.response?.data?.issue_key);
          attempt += 1;
          if (!collision || attempt >= 5) {
            throw error;
          }
          const number = Number.parseInt(issueKey.split("-")[1] ?? "0", 10);
          issueKey = `TASK-${number + 1}`;
        }
      }
    },
    onSettled: settle,
  });

  const remove = useMutation({
    mutationKey: KANBAN_MUTATION_KEY,
    mutationFn: (card: BoardCard) =>
      pb.collection("cards").delete(card.id, { requestKey: null }),
    onMutate: async (card) => {
      playSfx("delete");
      // No `snapshotCards` lookup needed — the mutation's own argument
      // already *is* the full previous card, so restoring on failure is
      // just reviving that one object rather than reading it back out of a
      // cache that is about to have it removed.
      await queryClient.cancelQueries({ queryKey: CARDS_KEY });
      const previous: CardSnapshot = new Map([[card.id, card]]);
      patchAll((cards) => cards.filter((entry) => entry.id !== card.id));
      return { previous };
    },
    onError: (_error, _card, context) => restoreCards(context?.previous),
    onSettled: settle,
  });

  return { move, save, create, remove };
}

/** Create a project. The slug is the stable identity the importer matches on. */
export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: KANBAN_MUTATION_KEY,
    mutationFn: (input: { name: string; slug: string; order: number }) =>
      pb.collection("projects").create<ProjectsResponse>(input, { requestKey: null }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: PROJECTS_KEY });
    },
  });
}

/**
 * Archive or unarchive a project (`archive.ts`). Nothing is deleted — the
 * board and its cards only drop out of every list until it comes back.
 *
 * Invalidates the cards as well as the projects: the card reads filter on the
 * project's `archived_at`, so they are stale the moment it changes.
 */
export function useSetProjectArchived() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: KANBAN_MUTATION_KEY,
    mutationFn: (input: { project: ProjectsResponse; archived: boolean }) =>
      pb.collection("projects").update<ProjectsResponse>(
        input.project.id,
        { archived_at: input.archived ? new Date().toISOString() : "" },
        { requestKey: null },
      ),
    // Write the saved record into the cache straight away: the caller switches
    // to the unarchived board on success, and until the refetch lands the
    // active list would not contain it yet, so the board would briefly fall
    // back to the first active project.
    onSuccess: (saved) => {
      queryClient.setQueryData<ProjectsResponse[]>(PROJECTS_KEY, (list) =>
        list?.map((project) => (project.id === saved.id ? saved : project)),
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: PROJECTS_KEY });
      void queryClient.invalidateQueries({ queryKey: CARDS_KEY });
    },
  });
}
